import { z } from 'zod'
import { HouseholdId, TodoId, UserId } from './ids.ts'
import { Mode, OwnerId } from './mode.ts'

export const TodoTitle = z.string().trim().min(1).max(200)
export type TodoTitle = z.infer<typeof TodoTitle>

// Always lowercase — categories are stored, transmitted and presented in
// lowercase, normalised here at parse time so callers don't have to think
// about it. "general" is reserved — the UI labels the null/uncategorised
// bucket "general" and treats it as the absence of a category.
export const TodoCategory = z
	.string()
	.trim()
	.toLowerCase()
	.min(1)
	.max(24)
	.refine((s) => s !== 'general', {
		message: '"general" is reserved',
	})
	.brand<'TodoCategory'>()
export type TodoCategory = z.infer<typeof TodoCategory>

export const Todo = z.object({
	id: TodoId,
	householdId: HouseholdId,
	ownerId: OwnerId,
	title: TodoTitle,
	completed: z.boolean(),
	createdAt: z.iso.datetime(),
	createdBy: UserId,
	completedAt: z.iso.datetime().nullable(),
	completedBy: UserId.nullable(),
	// Existing pre-categories rows have no `category` attribute in DDB —
	// `.optional().default(null)` lets them parse cleanly while keeping the
	// inferred TS type as `TodoCategory | null`.
	category: TodoCategory.nullable().optional().default(null),
	// Pre-priority rows have no `priority` attribute; default keeps them
	// parsing cleanly while the inferred TS type stays `boolean`.
	priority: z.boolean().optional().default(false),
	// Household members assigned to this task. Only meaningful on family
	// todos (personal todos are private to their owner). Pre-assignee rows
	// have no attribute; default keeps them parsing as an empty array.
	assigneeIds: z.array(UserId).optional().default([]),
})
export type Todo = z.infer<typeof Todo>

export const TodoListResponse = z.object({
	todos: z.array(Todo),
})
export type TodoListResponse = z.infer<typeof TodoListResponse>

export const TodoCreateBody = z.object({
	mode: Mode,
	title: TodoTitle,
	category: TodoCategory.nullable().optional(),
	priority: z.boolean().optional(),
	assigneeIds: z.array(UserId).optional(),
})
export type TodoCreateBody = z.infer<typeof TodoCreateBody>

export const TodoCreateResponse = z.object({ todo: Todo })
export type TodoCreateResponse = z.infer<typeof TodoCreateResponse>

// `category` semantics for update: omitted = leave alone, null = clear to
// general, string = set/replace. Same omit-vs-set rule for `priority`.
// `assigneeIds`: omitted = leave alone, [] = clear, array = replace.
export const TodoUpdateBody = z
	.object({
		title: TodoTitle.optional(),
		completed: z.boolean().optional(),
		category: TodoCategory.nullable().optional(),
		priority: z.boolean().optional(),
		assigneeIds: z.array(UserId).optional(),
	})
	.refine(
		(b) =>
			b.title !== undefined ||
			b.completed !== undefined ||
			b.category !== undefined ||
			b.priority !== undefined ||
			b.assigneeIds !== undefined,
		{
			message:
				'At least one of title, completed, category, priority or assigneeIds must be provided',
		},
	)
export type TodoUpdateBody = z.infer<typeof TodoUpdateBody>

export const TodoUpdateResponse = z.object({ todo: Todo })
export type TodoUpdateResponse = z.infer<typeof TodoUpdateResponse>

export const TodoDeleteResponse = z.object({ ok: z.literal(true) })
export type TodoDeleteResponse = z.infer<typeof TodoDeleteResponse>

// `category` on clear-completed: omitted = clear across all buckets, null =
// clear only the general (uncategorised) bucket, string = clear only that
// named bucket. `priority`: omitted = both, true = priorities only, false =
// non-priorities only.
export const TodoClearCompletedBody = z.object({
	mode: Mode,
	category: TodoCategory.nullable().optional(),
	priority: z.boolean().optional(),
})
export type TodoClearCompletedBody = z.infer<typeof TodoClearCompletedBody>

export const TodoClearCompletedResponse = z.object({
	deletedCount: z.number().int().min(0),
})
export type TodoClearCompletedResponse = z.infer<
	typeof TodoClearCompletedResponse
>
